import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { Button } from "@App/Components/Funnel/Button";
import { useTranslation } from "react-i18next";
import { Input } from "@App/Components/common/Input";
import { useLazyQuery } from "@apollo/client";
import {
  ContractDetailsQuery,
  GET_CONTRACT_DETAILS,
} from "@App/graphql/queries/fleetManagement/getContractDetails";
import { LoadingIcon } from "@App/Components/common/Icons";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators, State } from "@App/state";
import { FormErrors } from "@App/types/enums";
import { Headline } from "@App/Components/atoms/common/Headline";
import { ContractNumberHelpIcon } from "@App/Components/atoms/whitelabel/hints/ContractNumberHelpIcon";
import { OrganizationIsMeinAuto } from "@App/utils/conditionals/organization";

export type DeliveryIdentityProps = {
  onSubmit: () => void;
};

export const DeliveryIdentity: FunctionComponent<DeliveryIdentityProps> = ({
  onSubmit,
}: DeliveryIdentityProps) => {
  const { t } = useTranslation(); // i18next

  const dispatch = useDispatch();
  const { setContractDetails } = bindActionCreators(actionCreators, dispatch);

  const organization = useSelector((state: State) => state.orders.organization);
  const organizationId = organization.id;
  const [agreementNumber, setAgreementNumber] = useState("");
  const [lastName, setLastName] = useState("");

  const [getContractDetails, { error, loading, data }] = useLazyQuery<ContractDetailsQuery>(
    GET_CONTRACT_DETAILS,
    {
      variables: {
        organization: organizationId,
        agreementNumber,
        lastName,
      },
    },
  );

  useEffect(() => {
    if (data?.organization?.contractDetails?.earliestDeliveryDate) {
      setContractDetails(data.organization.contractDetails);
      onSubmit();
    }
  }, [data]);

  return (
    <Fragment>
      <div
        className={
          "container md:w-[600px] m-5 h-[calc(100vh-150px)] flex items-start md:items-center mx-auto"
        }
      >
        <div>
          <Headline type="h1">{t("vehicleDelivery.identity-headline")}</Headline>
          <p className={"text-neutral-500"}>{t("vehicleDelivery.identity-text")}</p>

          <div className="flex flex-col md:flex-row justify-start gap-5 mt-5 mb-3">
            <div className={"flex flex-col"}>
              <label htmlFor="name">{t("vehicleDelivery.identity-name")}</label>
              <Input
                id="name"
                type="text"
                className={"w-80 !border-neutral-300 !my-0"}
                value={lastName}
                placeholder={"Mustermann"}
                onChange={setLastName}
              />
            </div>

            <div className={"flex flex-col"}>
              <div className="flex items-center gap-2">
                <label htmlFor="agreementNumber">
                  {t("vehicleDelivery.identity-agreement-number")}
                </label>
                {OrganizationIsMeinAuto(organization) && <ContractNumberHelpIcon />}
              </div>
              <div className="flex items-center">
                <Input
                  id="agreementNumber"
                  type="text"
                  className={"!border-neutral-300 !my-0"}
                  value={agreementNumber}
                  placeholder={"1234567"}
                  onChange={setAgreementNumber}
                />
                {loading && <LoadingIcon className="animate-spin h-5 w-5 ml-3" />}
              </div>
            </div>
          </div>

          {error?.message === FormErrors.CONTRACT_NOT_FOUND && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.identity-error-contract")}</p>
            </div>
          )}

          {error?.message === FormErrors.LASTNAME_NOT_FOUND && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.identity-error-lastName")}</p>
            </div>
          )}

          {error?.message === FormErrors.DELIVERY_NOT_YET_POSSIBLE && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.identity-error-not-yet-possible")}</p>
            </div>
          )}

          {data && !data.organization?.contractDetails?.earliestDeliveryDate && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.identity-error-not-yet-possible")}</p>
            </div>
          )}

          {error?.message === FormErrors.DELIVERY_WISH_ALREADY_SET && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.identity-error-wish-already-set")}</p>
            </div>
          )}

          <Button
            title={t("vehicleDelivery.identity-button")}
            onClick={() => getContractDetails({ variables: { agreementNumber, lastName } })}
            disabled={!agreementNumber || !lastName || loading}
          />
        </div>
      </div>
    </Fragment>
  );
};
